import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { TextField, Box, FormControl, InputLabel, FormHelperText } from '@mui/material';
import FormSkeleton from '@components/FormSkeleton';
import CustomerSelect from '@components/customer/CustomerSelect';
import LangSelect from '@components/LangSelect';

const MailingListForm = forwardRef(({ formData, onSubmit, buttons, setNextDisabled, fieldsVariant="standard" }, ref) => {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        setNextDisabled && setNextDisabled(false);
    }, [setNextDisabled]);

    const validationSchema = Yup.object({
        customer_id: Yup.string()
            .required(t('This field is required')),
        name: Yup.string()
            .max(255, t('Must be {{max}} characters or less', { max: 255 }))
            .required(t('This field is required')),
        lang: Yup.string()
            .required(t('This field is required')),
    });

    const { control, handleSubmit, setValue, setError, formState: { errors, isValid } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        reValidateMode: 'onBlur',
    });
    useImperativeHandle(ref, () => ({ setError })); // Permettre au composant parent d'appeler setError

    useEffect(() => {
        setNextDisabled && setNextDisabled(!isValid);
    }, [isValid, setNextDisabled]);

    useEffect(() => {
        if (formData) {
            for (const [key, value] of Object.entries(formData)) {
                setValue(key, value);
            }
        }
        setLoaded(true);
    }, [formData, setValue]);

    if (!loaded) return <FormSkeleton nb={2} />;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>

            <Box mb={3}>
                <Controller
                    name="customer_id"
                    control={control}
                    render={({ field }) => (
                        <FormControl
                            fullWidth
                            variant={fieldsVariant}
                            required
                            error={Boolean(errors.customer_id)}
                        >
                            <InputLabel>{t('Customer')}</InputLabel>
                            <CustomerSelect
                                emptyLabel={t('- No selection -')}
                                {...field}
                            />
                            {errors.customer_id ? <FormHelperText>{errors.customer_id.message}</FormHelperText> : ''}
                        </FormControl>
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <TextField
                                {...field}
                                required
                                fullWidth
                                label={t('Name')}
                                variant={fieldsVariant}
                                error={Boolean(errors.name)}
                                helperText={errors.name?.message}
                            />
                        </div>
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            multiline
                            label={t('Description')}
                            variant={fieldsVariant}
                        />
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="lang"
                    control={control}
                    render={({ field }) => (
                        <FormControl
                            {...field}
                            fullWidth
                            required
                            variant={fieldsVariant}
                        >
                            <InputLabel>{t('Contacts language')}</InputLabel>
                            <LangSelect
                                emptyLabel={t('- No selection -')}
                                translatedInCurrentLanguage
                                {...field}
                            />
                        </FormControl>
                    )}
                />
            </Box>

            <Box sx={{ mt: 2 }}>
                {buttons}
            </Box>
        </form>
    );
});

export default MailingListForm;
